import type {
  AdminCompaniesResponseDto,
  AdminCompanyDetailDto,
  AdminParentOrganizationSelectDto,
  AdminParentOrganizationsSelectDto,
  CompanyFeatureFlag,
  CompanySelectDto,
} from '@@types/api.types';
import type { FiltersState } from '@components/data-table-filter/core/types';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { queryKeys } from '@queries/keys';
import { keepPreviousData, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

type Nullable<T> = T | null | undefined;

type AdminCompanyHierarchyOptionsDto = {
  childOrganizations: AdminParentOrganizationSelectDto[];
  parentOrganizations: AdminParentOrganizationSelectDto[];
};

type AdminUpdateOrganizationFeatureFlagsParams = {
  data: {
    featureFlags: CompanyFeatureFlag[];
  };
  id: string;
};

type AdminUpdateOrganizationHierarchyParams = {
  data: {
    childOrganizationIds: string[];
    isParentOrganization: boolean;
    parentOrganizationId: string | null;
  };
  id: string;
};

type AdminUpdateOrganizationParams = {
  data: {
    featureFlags?: CompanyFeatureFlag[];
    isParentOrganization?: boolean;
    parentOrganizationId?: string | null;
    slug?: string | null;
    tags?: string[];
  };
  id: string;
};

type AdminUpdateOrganizationSlugParams = {
  data: {
    slug: string | null;
  };
  id: string;
};

type AdminUpdateOrganizationTagsParams = {
  data: {
    tags: string[];
  };
  id: string;
};

export type AdminOrganizationsQueryParams = {
  filters?: Nullable<FiltersState>;
  limit?: Nullable<number>;
  page?: Nullable<number>;
  search?: Nullable<string>;
};

const buildQueryParams = (params: AdminOrganizationsQueryParams) => {
  const queryParams: Record<string, unknown> = {};

  if (params.search) {
    queryParams.search = params.search;
  }

  if (params.filters && params.filters.length > 0) {
    queryParams.filters = JSON.stringify(params.filters);
  }

  if (params.page) {
    queryParams.page = params.page;
  }

  if (params.limit) {
    queryParams.limit = params.limit;
  }

  return queryParams;
};

export const useAdminOrganizationsQuery = (params: AdminOrganizationsQueryParams) => {
  const { apiGet } = useApi();
  const queryParams = buildQueryParams(params);

  return useQuery<AdminCompaniesResponseDto, ApiErrorWrapper>({
    placeholderData: keepPreviousData,
    queryFn: async () => {
      return await apiGet<AdminCompaniesResponseDto>({
        path: '/v1/admin/companies',
        query: queryParams,
      });
    },
    queryKey: queryKeys.adminCompanies.getCompanies(queryParams),
    refetchOnWindowFocus: true,
  });
};

export const useAdminOrganizationDetailQuery = (companyId: string) => {
  const { apiGet } = useApi();

  return useSuspenseQuery<AdminCompanyDetailDto, ApiErrorWrapper>({
    queryFn: async () => {
      return await apiGet<AdminCompanyDetailDto>({
        path: `/v1/admin/companies/${companyId}`,
      });
    },
    queryKey: queryKeys.adminCompanies.getCompanyById(companyId),
  });
};

export const useAdminOrganizationHierarchyOptionsQuery = (companyId: string) => {
  const { apiGet } = useApi();

  return useQuery<AdminCompanyHierarchyOptionsDto, ApiErrorWrapper>({
    queryFn: async () => {
      return await apiGet<AdminCompanyHierarchyOptionsDto>({
        path: `/v1/admin/companies/${companyId}/hierarchy-options`,
      });
    },
    queryKey: queryKeys.adminCompanies.getHierarchyOptions(companyId),
  });
};

export const useAdminUpdateOrganization = () => {
  const { apiPatch } = useApi({ showToastOnError: false });
  const queryClient = useQueryClient();

  return useMutation<CompanySelectDto, ApiErrorWrapper, AdminUpdateOrganizationParams>({
    mutationFn: async ({ data, id }) => {
      return await apiPatch<CompanySelectDto>({
        body: data,
        path: `/v1/admin/companies/${id}`,
      });
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.root });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.getCompanyById(variables.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tags.root });
    },
  });
};

export const useAdminUpdateOrganizationFeatureFlags = () => {
  const { apiPatch } = useApi({ showToastOnError: false });
  const queryClient = useQueryClient();

  return useMutation<CompanySelectDto, ApiErrorWrapper, AdminUpdateOrganizationFeatureFlagsParams>({
    mutationFn: async ({ data, id }) => {
      return await apiPatch<CompanySelectDto>({
        body: data,
        path: `/v1/admin/companies/${id}/feature-flags`,
      });
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.root });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.getCompanyById(variables.id) });
    },
  });
};

export const useAdminUpdateOrganizationHierarchy = () => {
  const { apiPatch } = useApi({ showToastOnError: false });
  const queryClient = useQueryClient();

  return useMutation<CompanySelectDto, ApiErrorWrapper, AdminUpdateOrganizationHierarchyParams>({
    mutationFn: async ({ data, id }) => {
      return await apiPatch<CompanySelectDto>({
        body: data,
        path: `/v1/admin/companies/${id}/hierarchy`,
      });
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.root });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.getCompanyById(variables.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.getHierarchyOptions(variables.id) });
    },
  });
};

export const useAdminUpdateOrganizationSlug = () => {
  const { apiPatch } = useApi({ showToastOnError: false });
  const queryClient = useQueryClient();

  return useMutation<CompanySelectDto, ApiErrorWrapper, AdminUpdateOrganizationSlugParams>({
    mutationFn: async ({ data, id }) => {
      return await apiPatch<CompanySelectDto>({
        body: data,
        path: `/v1/admin/companies/${id}/slug`,
      });
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.root });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.getCompanyById(variables.id) });
    },
  });
};

export const useAdminUpdateOrganizationTags = () => {
  const { apiPatch } = useApi({ showToastOnError: false });
  const queryClient = useQueryClient();

  return useMutation<CompanySelectDto, ApiErrorWrapper, AdminUpdateOrganizationTagsParams>({
    mutationFn: async ({ data, id }) => {
      return await apiPatch<CompanySelectDto>({
        body: data,
        path: `/v1/admin/companies/${id}/tags`,
      });
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.root });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.getCompanyById(variables.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tags.root });
    },
  });
};

export const useAdminDeleteOrganization = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, ApiErrorWrapper, string>({
    mutationFn: async (id) => {
      await apiDelete({
        path: `/v1/admin/companies/${id}`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.root });
    },
  });
};

export const useAdminRestoreOrganization = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<CompanySelectDto, ApiErrorWrapper, string>({
    mutationFn: async (id) => {
      return await apiPost<CompanySelectDto>({
        path: `/v1/admin/companies/${id}/restore`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.root });
    },
  });
};

type AdminParentOrganizationsQueryParams = {
  includeDeleted?: boolean;
  tagNames?: string[];
};

export const useAdminParentOrganizationsQuery = (params?: AdminParentOrganizationsQueryParams) => {
  const { apiGet } = useApi();
  const queryParams: Record<string, unknown> = {};

  if (params?.includeDeleted !== undefined) {
    queryParams.includeDeleted = params.includeDeleted;
  }

  if (params?.tagNames && params.tagNames.length > 0) {
    queryParams.tagNames = params.tagNames;
  }

  return useQuery<AdminParentOrganizationsSelectDto, ApiErrorWrapper>({
    queryFn: async () => {
      return await apiGet<AdminParentOrganizationsSelectDto>({
        path: '/v1/admin/companies/parents',
        query: queryParams,
      });
    },
    queryKey: queryKeys.adminCompanies.getParentOrganizations(params),
  });
};
